import {
  BusinessSegmentConditions,
  ConditionOperatorEnumType,
  DateUnit,
  RelativeDateDirection,
} from '@reach/shared-types/conditions';

/**
 * Default segment templates that can be used to create new segments.
 *
 * These templates use PLACEHOLDER field names (e.g., <<PLACEHOLDER_total_spend>>)
 * to indicate where users need to swap in their own custom fields from their schema.
 *
 * After creating a segment from a template, users should:
 * 1. Open the SegmentBuilder
 * 2. Replace placeholder fields with actual fields from their data schema
 * 3. Adjust values/thresholds as needed for their business
 */
export type DefaultSegmentTemplate = {
  name: string;
  displayName: string;
  description: string;
  contents: BusinessSegmentConditions;
};

/**
 * Universal segment templates available to all partners.
 *
 * Each template includes:
 * - Placeholder field names that must be replaced with actual schema fields
 * - Appropriate operators for the use case
 * - Example values that can be adjusted
 *
 * PLACEHOLDER NAMING CONVENTION:
 * - <<PLACEHOLDER_field_name>> - Replace with your actual field name
 * - Values are examples and should be adjusted for your business
 */
export const DEFAULT_SEGMENT_TEMPLATES: DefaultSegmentTemplate[] = [
  {
    name: 'high_value_customers',
    displayName: '💎 High-Value Customers',
    description: 'Customers who have spent over $1,000 lifetime.',
    contents: [
      {
        field: '<<PLACEHOLDER_total_spend>>',
        operator: ConditionOperatorEnumType.GREATER_THAN,
        value: [1000],
      },
    ],
  },
  {
    name: 'lapsed_customers',
    displayName: '😴 Lapsed Customers',
    description: "Customers who haven't purchased in 60+ days.",
    contents: [
      {
        field: '<<PLACEHOLDER_last_purchase_date>>',
        operator: ConditionOperatorEnumType.LESS_THAN,
        value: [
          {
            type: 'relative' as const,
            value: 60,
            unit: DateUnit.DAYS,
            direction: RelativeDateDirection.BEFORE,
          },
        ],
      },
    ],
  },
  {
    name: 'frequent_buyers',
    displayName: '🔄 Frequent Buyers',
    description: 'Customers who have made 5+ purchases.',
    contents: [
      {
        field: '<<PLACEHOLDER_purchase_count>>',
        operator: ConditionOperatorEnumType.GREATER_THAN,
        value: [4],
      },
    ],
  },
  {
    name: 'first_time_buyers',
    displayName: '🆕 First-Time Buyers',
    description: 'Customers who have made exactly one purchase.',
    contents: [
      {
        field: '<<PLACEHOLDER_purchase_count>>',
        operator: ConditionOperatorEnumType.EQUALS,
        value: [1],
      },
    ],
  },
  {
    name: 'cart_abandoners',
    displayName: '🛒 Cart Abandoners',
    description: "Customers who added items but didn't complete purchase.",
    contents: [
      {
        field: '<<PLACEHOLDER_has_abandoned_cart>>',
        operator: ConditionOperatorEnumType.EQUALS,
        value: [true],
      },
    ],
  },
  {
    name: 'birthdays',
    displayName: '🎂 Upcoming Birthdays',
    description: 'Customers with birthdays in the next 30 days.',
    contents: [
      {
        field: '<<PLACEHOLDER_birthday>>',
        operator: ConditionOperatorEnumType.BETWEEN_MONTH_DAY,
        value: [
          {
            startDate: {
              type: 'relative' as const,
              value: 0,
              unit: DateUnit.DAYS,
              direction: RelativeDateDirection.BEFORE,
            },
            endDate: {
              type: 'relative' as const,
              value: 30,
              unit: DateUnit.DAYS,
              direction: RelativeDateDirection.AFTER,
            },
          },
        ],
      },
    ],
  },
];

/**
 * Get a segment template by name
 */
export function getSegmentTemplateByName(
  name: string,
): DefaultSegmentTemplate | undefined {
  return DEFAULT_SEGMENT_TEMPLATES.find(template => template.name === name);
}

/**
 * Get all segment template metadata (without contents)
 */
export function getSegmentTemplateMetadata() {
  return DEFAULT_SEGMENT_TEMPLATES.map(
    ({ name, displayName, description }) => ({
      name,
      displayName,
      description,
    }),
  );
}
